(() => {
  const SECTION_SELECTOR = '.hq-trusted-voices';
  const MOBILE_QUERY = window.matchMedia('(max-width: 767px)');

  function initTrustedVoices(section) {
    if (!section) return;

    if (
      section.dataset.hqTrustedVoicesReady === 'true'
    ) {
      return;
    }

    const shell = section.querySelector(
      '.hq-trusted-voices__carousel-shell'
    );

    const carousel = section.querySelector(
      '[data-hq-trusted-carousel]'
    );

    const cards = Array.from(
      section.querySelectorAll(
        '.hq-trusted-voices__voice'
      )
    );

    const dots = Array.from(
      section.querySelectorAll(
        '.hq-trusted-voices__pagination-dot'
      )
    );

    const prevButton = section.querySelector(
      '[data-hq-trusted-prev]'
    );

    const nextButton = section.querySelector(
      '[data-hq-trusted-next]'
    );

    if (
      !shell ||
      !carousel ||
      cards.length === 0
    ) {
      return;
    }

    section.dataset.hqTrustedVoicesReady = 'true';

    let ticking = false;

    let isDragging = false;
    let startX = 0;
    let startingScrollLeft = 0;
    let dragDistance = 0;


    /* =====================================================
       SHARED HELPERS
       ===================================================== */

    function getMaxScroll() {
      return Math.max(
        0,
        carousel.scrollWidth -
          carousel.clientWidth
      );
    }


    function getCardStep() {
      if (cards.length > 1) {
        return (
          cards[1].offsetLeft -
          cards[0].offsetLeft
        );
      }

      if (cards.length === 1) {
        return cards[0]
          .getBoundingClientRect()
          .width;
      }

      return 0;
    }


    /* =====================================================
       DESKTOP / TABLET ARROWS
       ===================================================== */

    function positionDesktopArrows() {
      if (MOBILE_QUERY.matches) return;

      const firstMedia =
        cards[0]?.querySelector(
          '.hq-trusted-voices__media'
        );

      if (!firstMedia) return;

      const shellRect =
        shell.getBoundingClientRect();

      const mediaRect =
        firstMedia.getBoundingClientRect();

      const arrowSize = 58;

      const top =
        mediaRect.top -
        shellRect.top +
        (
          mediaRect.height -
          arrowSize
        ) / 2;

      shell.style.setProperty(
        '--hq-trusted-arrow-top',
        `${top}px`
      );
    }


    function updateDesktopArrows() {
      if (MOBILE_QUERY.matches) return;

      const maxScroll =
        getMaxScroll();

      const currentScroll =
        Math.max(
          0,
          carousel.scrollLeft
        );

      const atStart =
        currentScroll <= 5;

      const atEnd =
        currentScroll >=
        maxScroll - 5;

      const cannotScroll =
        maxScroll <= 5;

      if (prevButton) {
        prevButton.classList.toggle(
          'is-hidden',
          atStart || cannotScroll
        );
      }

      if (nextButton) {
        nextButton.classList.toggle(
          'is-hidden',
          atEnd || cannotScroll
        );
      }
    }


    if (prevButton) {
      prevButton.addEventListener(
        'click',
        () => {
          if (MOBILE_QUERY.matches) {
            return;
          }

          const step =
            getCardStep();

          const target =
            Math.max(
              carousel.scrollLeft -
                step,
              0
            );

          carousel.scrollTo({
            left: target,
            behavior: 'smooth'
          });
        }
      );
    }


    if (nextButton) {
      nextButton.addEventListener(
        'click',
        () => {
          if (MOBILE_QUERY.matches) {
            return;
          }

          const step =
            getCardStep();

          const maxScroll =
            getMaxScroll();

          const target =
            Math.min(
              carousel.scrollLeft +
                step,
              maxScroll
            );

          carousel.scrollTo({
            left: target,
            behavior: 'smooth'
          });
        }
      );
    }


    /* =====================================================
       DESKTOP / TABLET MOUSE DRAG
       Mobile keeps native touch swipe.
       ===================================================== */

    carousel.addEventListener(
      'pointerdown',
      (event) => {
        if (MOBILE_QUERY.matches) {
          return;
        }

        if (
          event.pointerType === 'touch'
        ) {
          return;
        }

        if (
          event.pointerType === 'mouse' &&
          event.button !== 0
        ) {
          return;
        }

        isDragging = true;
        dragDistance = 0;

        startX =
          event.clientX;

        startingScrollLeft =
          carousel.scrollLeft;

        carousel.classList.add(
          'is-dragging'
        );

        try {
          carousel.setPointerCapture(
            event.pointerId
          );
        } catch (error) {
          /* Drag still works without pointer capture. */
        }

        event.preventDefault();
      }
    );


    carousel.addEventListener(
      'pointermove',
      (event) => {
        if (!isDragging) {
          return;
        }

        const movement =
          event.clientX -
          startX;

        dragDistance =
          Math.max(
            dragDistance,
            Math.abs(movement)
          );

        carousel.scrollLeft =
          startingScrollLeft -
          movement;

        event.preventDefault();
      }
    );


    function finishDrag(event) {
      if (!isDragging) {
        return;
      }

      isDragging = false;

      carousel.classList.remove(
        'is-dragging'
      );

      try {
        carousel.releasePointerCapture(
          event.pointerId
        );
      } catch (error) {
        /* Nothing to release. */
      }

      const step =
        getCardStep();

      if (step <= 0) {
        updateDesktopArrows();
        return;
      }

      const maxScroll =
        getMaxScroll();

      const closestCard =
        Math.round(
          carousel.scrollLeft /
          step
        );

      const target =
        Math.min(
          Math.max(
            closestCard * step,
            0
          ),
          maxScroll
        );

      carousel.scrollTo({
        left: target,
        behavior: 'smooth'
      });
    }


    carousel.addEventListener(
      'pointerup',
      finishDrag
    );

    carousel.addEventListener(
      'pointercancel',
      finishDrag
    );


    /* Prevent accidental click after a real desktop drag. */

    carousel.addEventListener(
      'click',
      (event) => {
        if (
          MOBILE_QUERY.matches ||
          dragDistance < 6
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        dragDistance = 0;
      },
      true
    );


    /* Keyboard navigation on desktop/tablet. */

    carousel.addEventListener(
      'keydown',
      (event) => {
        if (MOBILE_QUERY.matches) {
          return;
        }

        const step =
          getCardStep();

        if (step <= 0) {
          return;
        }

        if (
          event.key === 'ArrowRight'
        ) {
          event.preventDefault();

          carousel.scrollBy({
            left: step,
            behavior: 'smooth'
          });
        }

        if (
          event.key === 'ArrowLeft'
        ) {
          event.preventDefault();

          carousel.scrollBy({
            left: -step,
            behavior: 'smooth'
          });
        }
      }
    );


    /* =====================================================
       MOBILE PAGINATION
       Existing behavior preserved.
       ===================================================== */

    function setActive(index) {
      if (dots.length === 0) {
        return;
      }

      const safeIndex = Math.max(
        0,
        Math.min(
          index,
          cards.length - 1
        )
      );

      dots.forEach(
        (dot, dotIndex) => {
          const isActive =
            dotIndex === safeIndex;

          dot.classList.toggle(
            'is-active',
            isActive
          );

          dot.setAttribute(
            'aria-current',
            isActive
              ? 'true'
              : 'false'
          );
        }
      );
    }


    function positionPagination() {
      if (!MOBILE_QUERY.matches) {
        return;
      }

      const firstMedia =
        cards[0]?.querySelector(
          '.hq-trusted-voices__media'
        );

      if (!firstMedia) return;

      const shellRect =
        shell.getBoundingClientRect();

      const mediaRect =
        firstMedia.getBoundingClientRect();

      const top =
        mediaRect.bottom -
        shellRect.top +
        18;

      shell.style.setProperty(
        '--hq-trusted-pagination-top',
        `${top}px`
      );
    }


    function getNearestCardIndex() {
      const carouselRect =
        carousel.getBoundingClientRect();

      const targetX =
        carouselRect.left;

      let nearestIndex = 0;
      let nearestDistance = Infinity;

      cards.forEach(
        (card, index) => {
          const cardRect =
            card.getBoundingClientRect();

          const distance =
            Math.abs(
              cardRect.left -
              targetX
            );

          if (
            distance <
            nearestDistance
          ) {
            nearestDistance =
              distance;

            nearestIndex =
              index;
          }
        }
      );

      return nearestIndex;
    }


    function updateMobileFromScroll() {
      if (!MOBILE_QUERY.matches) {
        return;
      }

      setActive(
        getNearestCardIndex()
      );
    }


    dots.forEach(
      (dot, index) => {
        dot.addEventListener(
          'click',
          () => {
            if (
              !MOBILE_QUERY.matches
            ) {
              return;
            }

            const card =
              cards[index];

            if (!card) return;

            const carouselRect =
              carousel.getBoundingClientRect();

            const cardRect =
              card.getBoundingClientRect();

            const targetScroll =
              carousel.scrollLeft +
              cardRect.left -
              carouselRect.left;

            carousel.scrollTo({
              left: targetScroll,
              behavior: 'smooth'
            });

            setActive(index);
          }
        );
      }
    );


    /* =====================================================
       SHARED SCROLL / RESIZE UPDATE
       ===================================================== */

    function updateFromScroll() {
      if (ticking) {
        return;
      }

      ticking = true;

      requestAnimationFrame(
        () => {
          if (MOBILE_QUERY.matches) {
            updateMobileFromScroll();
          } else {
            updateDesktopArrows();
          }

          ticking = false;
        }
      );
    }


    carousel.addEventListener(
      'scroll',
      updateFromScroll,
      {
        passive: true
      }
    );


    function refreshLayout() {
      if (MOBILE_QUERY.matches) {
        positionPagination();

        setActive(
          getNearestCardIndex()
        );
      } else {
        positionDesktopArrows();
        updateDesktopArrows();
      }
    }


    window.addEventListener(
      'resize',
      refreshLayout,
      {
        passive: true
      }
    );


    MOBILE_QUERY.addEventListener(
      'change',
      () => {
        requestAnimationFrame(
          refreshLayout
        );
      }
    );


    /* =====================================================
       INITIAL STATE
       ===================================================== */

    requestAnimationFrame(
      refreshLayout
    );
  }


  /* =======================================================
     INITIALIZE
     ======================================================= */

  function initAll(root = document) {
    if (
      root.matches &&
      root.matches(
        SECTION_SELECTOR
      )
    ) {
      initTrustedVoices(root);
    }

    root
      .querySelectorAll(
        SECTION_SELECTOR
      )
      .forEach(
        initTrustedVoices
      );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAll();
      }
    );
  } else {
    initAll();
  }


  /* Shopify Theme Editor */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );
})();